import { FC, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { UserCircleIcon } from 'lucide-react';

import { useValidateRecoveryTokenQuery } from '../services/apiSlice';
import { restoreFromSession } from '../features/signUp/signUpSlice';
import { store } from '../stores/AppStore';

import { Loading } from '../components/Loading';
import { Skeleton } from '../components/ui/skeleton';

const SignUpRecover: FC = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const {
    data: sessionData,
    isLoading,
    error,
  } = useValidateRecoveryTokenQuery(token ?? '', { skip: !token });

  useEffect(() => {
    if (!token || token.length !== 64) {
      toast.error('Invalid recovery link!');
      navigate('/sign-up');
      return;
    }

    if (error) {
      const errorMessage =
        (error as any)?.data?.message ||
        'Recovery link has expired or is invalid';

      if (/expired/gi.test(errorMessage)) {
        toast.error(
          'Recovery link has expired. Please start the sign-up process again.',
        );
      } else {
        toast.error(
          'Invalid recovery link. Please start the sign-up process again.',
        );
      }

      navigate('/sign-up');
      return;
    }

    if (sessionData) {
      // Restore the session state
      // Use the session email as the primary email source
      const formDataWithCorrectEmail = {
        ...sessionData.formData,
        mentor: {
          ...sessionData.formData.mentor,
          email: sessionData.email, // Use session email as the source of truth
        },
      };

      store.dispatch(
        restoreFromSession({
          sessionId: sessionData.sessionId,
          currentStep: sessionData.currentStep,
          formData: formDataWithCorrectEmail,
        }),
      );

      toast.success('Welcome back! Continuing where you left off...');
      navigate('/sign-up?recovered=true');
    }
  }, [token, sessionData, error, navigate]);

  return (
    <div
      className="
        flex flex-col gap-4 md:gap-6
        w-full md:w-[60%] lg:w-[50%]
        items-center
        mx-auto px-4 py-8 md:px-6 lg:px-8
        bg-gray-100 rounded-xl
      "
    >
      <h1 className="text-2xl md:text-3xl font-medium text-center">
        Continuing Your Sign-up
      </h1>
      <p className="text-gray-600 text-center">
        We're restoring your progress and will continue where you left off...
      </p>

      {isLoading ? (
        <>
          <Skeleton className="h-10 w-48 rounded-full bg-blue-100 mx-auto" />
          <div className="flex flex-col items-center gap-4">
            <Loading />
            <p className="text-sm text-gray-500">Validating recovery link...</p>
          </div>
        </>
      ) : sessionData ? (
        <>
          <div
            className="
              bg-blue-100 text-gray-600
              flex items-center gap-4
              px-3 py-2 rounded-full w-max mx-auto
              font-medium text-white bg-tttDefault
            "
          >
            <UserCircleIcon className="h-5 w-5 text-tttDefault" />
            {sessionData.email}
          </div>
          <div className="flex flex-col items-center gap-4">
            <Loading />
            <p className="text-sm text-gray-500">
              Restoring your sign-up progress...
            </p>
          </div>
        </>
      ) : undefined}
    </div>
  );
};

export default SignUpRecover;
